/**
 * The occupancy calendar for a phone (UI5).
 *
 * WHY A SECOND PRESENTATION AND NOT A SCROLLBAR. The desktop matrix is 1185px
 * wide at twelve months, and 200px of that is the sticky room column. At 500px
 * the content area is 466px, so the room name eats 43% of the screen and 4.4
 * months are left; at 320px it eats 69% and barely one month survives. Sideways
 * scrolling through a nine-column grid on a phone is not a small version of the
 * matrix, it is a worse way to read the same data. So the axis turns: months go
 * down the page instead of across it.
 *
 * IT DECIDES NOTHING. This renders the SAME `model` object the table renders —
 * same `groups`, same order, same `cells`, same `aggregates`, same
 * `transitions`. It does not group tenancies, classify a month, normalise a
 * transition, aggregate a loss or derive a free reason. Every one of those
 * questions was answered by `lifecycleCalendarTruth` before this component was
 * called, and answering any of them again here is how a product ends up
 * telling a manager one thing on a laptop and another on a phone.
 *
 * NOT A FAKE TABLE. The layout is no longer a matrix, so imitating one with
 * ARIA would describe a grid that is not on screen. It is what it looks like:
 * a section per unit, a list of rooms, and a list of months per room — which
 * is also the order the data should be read in.
 *
 * NOTHING IS CONVEYED BY COLOUR ALONE, exactly as in the cell: every month
 * prints its German state label beside its swatch.
 */
import React from "react";

import CalendarTransitionPopover from "./CalendarTransitionPopover";
import { STATE_CLASSES } from "./CoLivingOccupancyCell";
import { UNKNOWN_STATE } from "./occupancyCalendarModel";
import {
  PARTIAL_LABEL,
  potentialLossDisplay,
} from "../../../utils/lifecycleCalendarTruth";

/** The three counted figures, in the order the desktop summary prints them. */
const SUMMARY_FIGURES = [
  { key: "secure", label: "Sicher" },
  { key: "cancellable", label: "Kündbar" },
  { key: "free", label: "Frei" },
];

/**
 * One month of one room.
 *
 * The swatch carries the same classes as the desktop cell and the same
 * `data-lifecycle-*` attributes, so a test can assert the two agree without
 * either side owning a second mapping.
 */
function MonthRow({ cell, roomName }) {
  const stateClass = STATE_CLASSES[cell.state] || STATE_CLASSES[UNKNOWN_STATE];
  const transitions = cell.transitions || [];

  return (
    <li
      data-mobile-month={cell.monthKey}
      data-lifecycle-state={cell.state}
      data-lifecycle-family={cell.family}
      data-lifecycle-transitions={transitions.length || undefined}
      className={`flex min-w-0 items-center gap-2 rounded-[8px] border px-2.5 py-1.5 ${stateClass} ${
        cell.isCurrent ? "ring-1 ring-inset ring-primary/40" : ""
      }`}
    >
      <span className="w-[62px] shrink-0 text-[11px] font-semibold uppercase tracking-[0.04em] tabular-nums opacity-80">
        {cell.monthLabel}
      </span>
      <span className="min-w-0 flex-1 break-words text-[12px] font-semibold leading-tight">
        {cell.label}
      </span>
      {cell.state === "notice_given" ? (
        <span
          className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500"
          aria-hidden="true"
        />
      ) : null}
      {/*
        THE SAME PRIMITIVE THE DESKTOP CELL USES. A phone is precisely the
        device R8's tooltip could not serve, so a second mobile-only dialog
        here would be a new thing to keep in step with the one that already
        works on touch and on a keyboard.
      */}
      <CalendarTransitionPopover
        transitions={transitions}
        roomName={roomName}
        monthLabel={cell.monthLabel}
        // A finger, not a cursor: the matrix's 15px glyph is about a third of
        // the 44-48px the platform guidelines ask for, and this is the device
        // it matters on. Same glyph, same popover, a target you can hit.
        triggerClassName="-mr-1 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[6px] text-[13px] font-normal leading-none opacity-80 transition-colors hover:bg-foreground/10 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
    </li>
  );
}

/**
 * The trailing figures, for a room or for a unit.
 *
 * Same numbers and same R9 vocabulary as the desktop summary columns — a
 * manager who compares the phone with the laptop must not meet two different
 * words for one gap. Only the arrangement differs: a row of small figures
 * instead of four columns.
 */
function SummaryBlock({ aggregates, scopeAttr, label }) {
  const loss = potentialLossDisplay(aggregates);

  return (
    <div
      className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1 border-t border-border/60 pt-2 text-[11px] text-muted-foreground"
      data-mobile-summary={label}
    >
      {SUMMARY_FIGURES.map((f) => (
        <span key={f.key} className="tabular-nums" {...{ [scopeAttr]: f.key }}>
          {f.label} <span className="font-semibold text-foreground">{aggregates[f.key]}</span>
        </span>
      ))}
      <span
        className="min-w-0 tabular-nums"
        {...{ [scopeAttr]: "lostRevenue" }}
        data-loss-kind={loss.kind}
        title={loss.accessibleText || undefined}
      >
        Pot. Verlust{" "}
        <span className="font-semibold text-foreground">{loss.amountLabel}</span>
        {loss.partial ? (
          <span className="font-normal opacity-80"> · {PARTIAL_LABEL}</span>
        ) : null}
        {loss.partial ? (
          <span data-loss-gap className="block leading-tight">
            {loss.gapText}
          </span>
        ) : null}
        {loss.srText ? <span className="sr-only"> {loss.srText}</span> : null}
      </span>
    </div>
  );
}

export default function CoLivingOccupancyMobile({ model }) {
  const groups = model?.groups || [];
  if (groups.length === 0) return null;

  return (
    <div data-occupancy-mobile className="flex flex-col gap-3 p-3">
      {groups.map((group) => {
        const headingId = `mobile-unit-${group.unitId}`;
        return (
          <section
            key={group.unitId}
            data-mobile-unit={group.displayId}
            aria-labelledby={headingId}
            className="rounded-[10px] border border-border bg-card"
          >
            <div className="border-b border-border bg-muted/50 px-3 py-2">
              <h3 id={headingId} className="m-0 flex min-w-0 flex-wrap items-baseline gap-x-2">
                <span className="font-mono text-[12px] font-semibold text-foreground">
                  {group.displayId}
                </span>
                {group.addressLine1 ? (
                  <span className="min-w-0 break-words text-[11px] font-normal text-muted-foreground">
                    {group.addressLine1}
                  </span>
                ) : null}
              </h3>
            </div>

            {group.roomCount === 0 ? (
              <p className="m-0 px-3 py-3 text-[11px] text-muted-foreground">
                Keine Rooms für diese Unit erfasst.
              </p>
            ) : (
              <ul className="m-0 flex list-none flex-col gap-3 p-3">
                {group.rooms.map((room) => (
                  <li key={room.key} data-mobile-room={room.name} className="min-w-0">
                    {/* ROOM-ACT-4, same marker as the table. The two
                        presentations are mutually exclusive (matchMedia, not
                        CSS), so a marker on only one of them would leave every
                        phone showing a retired room as ordinary inventory. */}
                    <h4 className="m-0 flex min-w-0 flex-wrap items-center gap-1.5 break-words text-[12px] font-semibold leading-snug text-foreground">
                      <span className="min-w-0 break-words">{room.name}</span>
                      {room.isActive === false ? (
                        <span className="shrink-0 rounded-full border border-border bg-muted px-1.5 py-px text-[9px] font-semibold text-muted-foreground">
                          Inaktiv
                        </span>
                      ) : null}
                    </h4>
                    <p className="m-0 mt-0.5 break-words text-[10px] leading-tight text-muted-foreground">
                      {room.meta}
                    </p>

                    <ul className="m-0 mt-2 flex list-none flex-col gap-1 p-0">
                      {room.cells.map((cell) => (
                        <MonthRow key={cell.monthKey} cell={cell} roomName={room.name} />
                      ))}
                    </ul>

                    <SummaryBlock
                      aggregates={room.aggregates}
                      scopeAttr="data-room-total"
                      label={room.name}
                    />
                  </li>
                ))}
              </ul>
            )}

            <div className="border-t border-border bg-muted/30 px-3 pb-3">
              <SummaryBlock
                aggregates={group.aggregates}
                scopeAttr="data-unit-total"
                label={group.displayId}
              />
            </div>
          </section>
        );
      })}
    </div>
  );
}
